import { Loader } from '@components/loader/Loader'
import { useSettlementDetail } from '@services/settlements/queries/useSettlementDetail'
import { useSettlementPdf } from '@services/settlements/queries/useSettlementPdf'
import { FileDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

import { SettlementBalanceRow } from '../../components/SettlementBalanceRow'
import { SettlementBlockTable } from '../../components/SettlementBlockTable'
import { formatDate } from '../../components/format'

const GROUPS = ['services', 'electricity', 'other'] as const

/** The tenant view: the breakdown, without the checks or the inputs. */
export function UserSettlementDetail() {
  const { id } = useParams()
  const { t } = useTranslation('settlement')
  const { data, isLoading } = useSettlementDetail({ id })
  const pdf = useSettlementPdf()

  if (isLoading) return <Loader />
  if (!data) return <div>{t('user.empty')}</div>

  const blocks = data.result.blocks.filter((block) => block.lines.length > 0)
  const groups = GROUPS.filter(
    (key) =>
      Number(data.result[key].cost) !== 0 ||
      Number(data.result[key].advances) !== 0
  )

  return (
    <div className="space-y-6">
      <Link
        to="/user/contract?tab=settlements"
        className="text-sm text-muted-foreground no-underline hover:text-ink"
      >
        ← {t('user.title')}
      </Link>

      <Card className="flex flex-wrap items-center justify-between gap-4 p-5">
        <div>
          <h1 className="m-0 font-display text-2xl font-bold tracking-tight text-ink">
            {data.title}
          </h1>
          <p className="m-0 text-sm text-muted-foreground">
            {formatDate(data.period_start)} – {formatDate(data.period_end)} ·{' '}
            {data.flat.address}
          </p>
        </div>

        <Button
          variant="outline"
          onClick={() => pdf.mutate(data.id)}
          disabled={pdf.isPending}
        >
          <FileDown className="mr-1.5 h-4 w-4" />
          {t('pdf.download')}
        </Button>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {blocks.map((block) => (
          <SettlementBlockTable key={block.code} block={block} />
        ))}
      </div>

      <Card className="p-5">
        {groups.map((key) => (
          <SettlementBalanceRow
            key={key}
            label={t(`result.groups.${key}`)}
            balance={data.result[key]}
          />
        ))}

        <div className="mt-3 border-t-2 border-primary pt-3">
          <SettlementBalanceRow
            label={t('result.grand_total')}
            emphasis
            balance={{
              cost: data.result.services.cost,
              advances: data.result.services.advances,
              balance: data.result.grand_total
            }}
          />
        </div>
      </Card>

      {data.tenant_note && (
        <Card className="p-5">
          <p className="m-0 text-sm text-ink">{data.tenant_note}</p>
        </Card>
      )}
    </div>
  )
}
